import * as ReferralsStyles from './Referrals.styles';
import ReferralInfo from './components/ReferralInfo';
import RewardSteps from './components/RewardSteps';
import Rewards from './components/Rewards';

import OrderHub from '../OrderHub';
import LoadingContainer from '../LoadingContainer';
import tracking, { setPageName, setPageRendered } from '../../utils/tracking';
import { PublicRoutePath } from '../../utils/routes';
import { TrackingEvent } from '../../utils/tracking/types';
import { eligibleToMakeReferrals$, profileState$, referralBonus$ } from '../../store/profile/selectors';
import { fetchReferralsPerform } from '../../store/referrals/actions';
import { getBrandContent } from '../../store/features/brandingContent';
import { referrals$ } from '../../store/referrals/selectors';
import PageHead from '../PageHead';
import { currencyDefaults, locale } from '../../utils/intl';

import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Typography from '@mui/material/Typography';
import TabName from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';

import type { ReactElement, SyntheticEvent } from 'react';

function Referrals() {
  const [activeTab, setActiveTab] = useState(1);
  const dispatch = useDispatch();
  const brandingContent = getBrandContent();

  const rewards = useSelector(referrals$);
  const profile = useSelector(profileState$);
  const eligibleToMakeReferrals = useSelector(eligibleToMakeReferrals$);

  useEffect(() => {
    setPageName(TrackingEvent.REFERRAL_LAND);
    setPageRendered();
    tracking.event(TrackingEvent.REFERRAL_LAND);
    dispatch(fetchReferralsPerform());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { classes } = ReferralsStyles.useWrapper({ rewardsPresent: rewards && !!rewards.length });

  const referralBonus = useSelector(referralBonus$);
  const referralBonusText = referralBonus
    ? `${Intl.NumberFormat(locale, currencyDefaults).format(referralBonus).replace('.00', '')} worth of`
    : '';

  if (!profile?.referralSlug) return null;

  const inviteRoute = PublicRoutePath.REFER.replace(':slug', profile.referralSlug ?? '');

  const shortInviteLinkPath = `${window.location.protocol}//${window.location.host}${inviteRoute}`;

  const isShareSupported = !!navigator.share;

  const onClickRemindHandler = async () => {
    // We place the URL within the text as some apps will ignore the URL field if the text field is populated and vice versa.
    const shareData = {
      title: `Book concrete the easy way with ${brandingContent.fullName}`,
      text: `Don't forget to try ${brandingContent.fullName}! We'll both get ${referralBonusText} Concrete Cash after your first order.`,
      url: shortInviteLinkPath,
    };

    tracking.event(TrackingEvent.REFERRAL_SEND_REMINDER);
    await navigator.share(shareData);
  };

  const tabChangeHandler = (_e: SyntheticEvent<Element, Event>, value: number) => {
    setActiveTab(value);
  };

  return (
    <>
      <PageHead title="REFERRALS" />
      <div className={classes.root}>
        <Box className={classes.titleBox}>
          <Typography className={classes.mobileH4} variant="h3" component="h3">
            Refer a tradie
          </Typography>
          {rewards && !!rewards.length && (
            <Box display={{ xs: 'block', sm: 'none' }}>
              <TabName
                classes={{ indicator: classes.tabIndicator }}
                value={activeTab}
                onChange={tabChangeHandler}
                aria-label="referral information tabs"
              >
                <Tab label="Referral link" value={1} />
                <Tab label="Rewards" value={2} />
              </TabName>
            </Box>
          )}
        </Box>
        <Suspense fallback={<LoadingContainer />}>
          <Grid className={classes.container} container>
            {activeTab === 1 ? (
              <>
                <Grid item xs={12} md={8} xl={8}>
                  <ReferralInfo
                    shortInviteLinkPath={shortInviteLinkPath}
                    inviteRoute={inviteRoute}
                    referralSlug={profile.referralSlug}
                    isShareSupported={isShareSupported}
                  />
                </Grid>
                <Grid item xs={12} md={4} xl={4}>
                  {eligibleToMakeReferrals ? (
                    <Box className={`${classes.card} ${classes.rightCard}`} display={{ xs: 'none', sm: 'block' }}>
                      <Typography variant="h4" component="h4">
                        Rewards
                      </Typography>
                      <Rewards
                        items={rewards}
                        isShareSupported={isShareSupported}
                        // eslint-disable-next-line @typescript-eslint/no-misused-promises
                        onClickHandler={onClickRemindHandler}
                      />
                    </Box>
                  ) : (
                    <RewardSteps />
                  )}
                </Grid>
              </>
            ) : (
              !!eligibleToMakeReferrals && (
                <Box className={`${classes.card} ${classes.rightCard}`} display={{ xs: 'block', sm: 'none' }}>
                  {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
                  <Rewards items={rewards} isShareSupported={isShareSupported} onClickHandler={onClickRemindHandler} />
                </Box>
              )
            )}
          </Grid>
        </Suspense>
      </div>
    </>
  );
}

Referrals.getLayout = function getLayout(page: ReactElement) {
  return <OrderHub>{page}</OrderHub>;
};

export default Referrals;
